import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Drawer, Empty, Spin, Table, Tag, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import * as api from "./controller";

/**
 * What the FTC team did on a given day, and what is still waiting.
 *
 * Both sections are a ROSTER LINE, not a matrix: one row per person carrying
 * their own clinics. The first version gave every office its own column, which
 * looked reasonable with three offices and fell apart at eleven — an FTC covers
 * one to three clinics, so the grid ran ~90% empty, scrolled sideways, and
 * changed width whenever the office count did (13 columns one day, 11 the next,
 * because the axis is derived from whichever rows exist). Only the Unassigned
 * row was ever dense, and it was forcing that width on everybody.
 *
 * So the office axis became local to each row. The table now grows DOWN with
 * the team instead of sideways with the platform, which is the direction that
 * scales — a person's coverage stays small however many clinics we sign.
 *
 * "Handled by" resolves through the same path the Insurance Verification page
 * uses — the request's recorded `fulfilled_by` first, then `attribution`, which
 * reads the artifact's author or the scrape job's initiator. The two screens
 * therefore always name the same person for the same request.
 *
 * Handled is two lanes: requests fulfilled that day (dated by the "mark done"
 * click — the manager's choice) plus work nobody asked for, dated by creation
 * because it has no click. Every number splits Manual / Agentic / Proactive on
 * hover. Outstanding counts only what is on the team's desk — the same filter
 * FTC Tasks is built from — and splits by carrier on hover.
 *
 * Not every handler is an FTC: offices close their own work too, and on a real
 * day most named people were office accounts. Each person carries the role
 * attribution assigned, shown beside the name, because folding office staff
 * silently into an FTC ranking would overstate the team — and dropping them
 * would stop the totals adding up.
 *
 * Three rows are deliberately NOT people and are labelled as such:
 *   "Agent"        work a scraper finished. A scrape job has no owner, and
 *                  crediting it to whoever triggered it flatters the wrong number.
 *   "Unattributed" closed before we recorded who closed it. Shown rather than
 *                  hidden, because a missing name is a fact about our records.
 *   "Unassigned"   open work with no owner. Everything raised before routing
 *                  existed lands here, and hiding it would make the per-person
 *                  totals look complete when they are not.
 * None of them gets a load bar: the bar encodes rank among people, which is what
 * the section is sorted by, and a capped bar on Unassigned's 69 sitting beside a
 * person's 10 read as equal load.
 *
 * The date picker belongs to HANDLED alone. Outstanding is a live snapshot: we
 * keep no history of what was open on a past day, so letting the date move it
 * would show today's backlog under yesterday's heading.
 */

const TYPE_LABELS = {
  breakdown: "Breakdown",
  estimate: "Estimate",
  claim_followup: "Follow-up",
};

// attribution.ROLE_* — the FTC team is the subject of this page, so only the
// other roles need spelling out.
const ROLE_LABELS = {
  super_admin: "Smilepass",
  office_admin: "Office admin",
  office_staff: "Office",
  unknown: "Unknown role",
};

const SPECIAL = {
  agent: { label: "Agent", glyph: "⚙", hint: "Fulfilled by a scraper — no person owns it." },
  unattributed: {
    label: "Unattributed",
    glyph: "?",
    hint: "Closed before the handler was recorded.",
  },
  unassigned: { label: "Unassigned", glyph: "⚑", hint: "Open work with no owner." },
};

// An office nobody covers can never be routed, so its work sits in Unassigned
// no matter how often the rebalancer runs. That is a staffing gap to fix on the
// FTC users tab, not a queue to wait on — so it is called out rather than left
// to look like ordinary backlog.
const NO_COVER_HINT =
  "No FTC has access to this office, so its requests can't be assigned to anyone. " +
  "Grant access on the FTC users tab.";

// Past this, the chips stop being scannable and start being a paragraph. The cap
// only ever bites on Unassigned; a person's own coverage is far below it.
const CHIP_CAP = 8;

// Every printed number in Handled carries a three-way split so a manager can
// hover and see HOW the work was done, not just how much:
//   Manual     a person did it in answer to a request (typed it in, uploaded a
//              PDF, or verified by phone and marked it done)
//   Agentic    a scraper or phone agent produced it — credited to whoever
//              triggered it; the "Agent" row is only for runs nobody triggered
//   Proactive  a person did it and nobody had asked
const MODE_LABELS = { manual: "Manual", agentic: "Agentic", proactive: "Proactive" };
const MODE_ORDER = ["manual", "agentic", "proactive"];

function modeTitle(split) {
  if (!split) return null;
  const parts = MODE_ORDER.filter((k) => split[k]).map((k) => `${split[k]} ${MODE_LABELS[k]}`);
  return parts.length ? parts.join(" · ") : null;
}

// Outstanding numbers split by carrier — that is what decides which portal or
// phone line the work needs. Biggest first.
function carrierTitle(split) {
  if (!split) return null;
  const parts = Object.keys(split)
    .filter((k) => split[k])
    .sort((a, b) => split[b] - split[a] || a.localeCompare(b))
    .map((k) => `${k} ${split[k]}`);
  return parts.length ? parts.join(" · ") : null;
}

function splitTitle(row, kind) {
  return kind === "carrier" ? carrierTitle(row) : modeTitle(row);
}

function Num({ value, split, kind, className }) {
  const title = value ? splitTitle(split, kind) : null;
  const el = <span className={className}>{value || "—"}</span>;
  return title ? <Tooltip title={title}>{el}</Tooltip> : el;
}

function ActorCell({ row }) {
  const special = SPECIAL[row.actor];
  if (special) {
    return (
      <div className="ftcd-who">
        <div className="ftcd-av ftcd-av-sp">{special.glyph}</div>
        <div className="ftcd-who-text">
          <div className="ftcd-name ftcd-name-sp">{special.label}</div>
          <div className="ftcd-role">{special.hint}</div>
        </div>
      </div>
    );
  }
  const person = row.person || {};
  const name = person.name || "—";
  const role = ROLE_LABELS[person.role];
  return (
    <div className="ftcd-who">
      <div className="ftcd-av">{name.trim().charAt(0).toUpperCase() || "?"}</div>
      <div className="ftcd-who-text">
        <Tooltip title={person.role_label ? `${name} — ${person.role_label}` : name}>
          <div className="ftcd-name">{name}</div>
        </Tooltip>
        {role ? <div className="ftcd-role">{role}</div> : null}
      </div>
    </div>
  );
}

function TotalCell({ row, peak, kind }) {
  const title = splitTitle(row[kind], kind);
  const inner = SPECIAL[row.actor] ? (
    <div className="ftcd-load">
      <b>{row.total}</b>
    </div>
  ) : (
    <div className="ftcd-load">
      <div className="ftcd-track">
        <div
          className="ftcd-fill"
          style={{ width: `${peak ? Math.min(100, Math.round((100 * row.total) / peak)) : 0}%` }}
        />
      </div>
      <b>{row.total}</b>
    </div>
  );
  return title ? <Tooltip title={title}>{inner}</Tooltip> : inner;
}

function officeItems(row, officeById, types, kind) {
  return Object.keys(row.cells || {})
    .map((oid) => {
      const cell = row.cells[oid] || {};
      const office = officeById[String(oid)] || {};
      return {
        id: String(oid),
        cell,
        split: (row[`cell_${kind}`] || {})[String(oid)],
        total: types.reduce((n, t) => n + (cell[t] || 0), 0),
        name: office.name || `Office ${oid}`,
        covered: office.covered !== false,
      };
    })
    .filter((i) => i.total > 0)
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

function chipTitle(item, types, kind) {
  const byType = types
    .filter((t) => item.cell[t])
    .map((t) => `${TYPE_LABELS[t] || t}: ${item.cell[t]}`)
    .join(" · ");
  const byVia = splitTitle(item.split, kind);
  return (
    <span>
      {item.name}
      {byType ? <br /> : null}
      {byType}
      {byVia ? <br /> : null}
      {byVia}
      {item.covered ? null : (
        <>
          <br />
          {NO_COVER_HINT}
        </>
      )}
    </span>
  );
}

function OfficesCell({ row, officeById, types, kind, onChipClick }) {
  const items = officeItems(row, officeById, types, kind);
  if (!items.length) return <span className="ftcd-zero">—</span>;
  const shown = items.slice(0, CHIP_CAP);
  const rest = items.slice(CHIP_CAP);
  return (
    <div className="ftcd-chips">
      {shown.map((i) => (
        <Tooltip key={i.id} title={chipTitle(i, types, kind)}>
          <button
            type="button"
            className={`ftcd-chip ftcd-chip-btn${i.covered ? "" : " ftcd-chip-gap"}`}
            onClick={() => onChipClick(row, i)}
          >
            <span className="ftcd-chip-n">{i.total}</span>
            <span className="ftcd-chip-t">{i.name}</span>
          </button>
        </Tooltip>
      ))}
      {rest.length ? (
        <Tooltip title={rest.map((i) => `${i.name}: ${i.total}`).join(" · ")}>
          <span className="ftcd-more">+{rest.length} more</span>
        </Tooltip>
      ) : null}
    </div>
  );
}

// This page is about the FTC team, but the handler is often an office closing
// its own work — on a real day two FTCs and four office accounts. Those rows are
// real and the totals need them, so they are folded away rather than dropped,
// and the count of what is hidden is always on screen.
function isOtherPerson(r) {
  return !SPECIAL[r.actor] && (r.person || {}).role !== "ftc";
}

/**
 * The requests behind one Handled number.
 *
 * A drawer rather than a link out, because no existing page can show this set:
 * FTC Tasks is built from an outstanding-work queue and cannot show fulfilled
 * requests at all, and Insurance Verification has neither a date nor a handler
 * filter and is scoped by the global office selector. Both would have answered a
 * different question than the one the number asked.
 */
function HandledDrawer({ target, onClose }) {
  const [state, setState] = useState({ loading: true, rows: [], count: 0 });

  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    setState({ loading: true, rows: [], count: 0 });
    api
      .getFtcDashboardRequests({
        date: target.date,
        actor: target.actor,
        office: target.officeId,
        type: target.type,
      })
      .then((res) => {
        if (cancelled) return;
        const body = (res.status >= 200 && res.status < 300 && res.json) || {};
        setState({ loading: false, rows: body.results || [], count: body.count || 0 });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, rows: [], count: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const title = target
    ? [target.who, target.officeName, target.date].filter(Boolean).join(" · ")
    : "";

  return (
    <Drawer
      open={!!target}
      onClose={onClose}
      width={560}
      title={title}
      destroyOnClose
    >
      {state.loading ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spin />
        </div>
      ) : !state.rows.length ? (
        <Empty description="Nothing to show." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <div className="crm-sub" style={{ marginBottom: 12 }}>
            {state.count} handled
          </div>
          {state.rows.map((r) => (
            <div key={r.id} className="ftcd-drawer-row">
              <div className="ftcd-drawer-main">
                <div className="crm-cell-strong">{r.patient}</div>
                <div className="crm-sub">
                  {[r.carrier !== "Unknown carrier" ? r.carrier : null, r.member_id]
                    .filter(Boolean)
                    .join(" · ") || "No policy on file"}
                </div>
              </div>
              <div className="ftcd-drawer-meta">
                <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                  {TYPE_LABELS[r.request_type] || r.request_type}
                </Tag>
                <div className="crm-sub">
                  {[
                    MODE_LABELS[r.mode] || r.mode,
                    r.kind !== "request" ? "no request" : null,
                    r.inferred ? "link inferred" : null,
                    r.fulfilled_at ? dayjs(r.fulfilled_at).format("HH:mm") : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}

function Section({ title, subtitle, data, types, offices, emptyText, actorHeader, extra,
                   foldOthers = false, onChipClick, kind = "mode" }) {
  const allRows = (data && data.rows) || [];
  const [showOthers, setShowOthers] = useState(false);

  const hidden = foldOthers && !showOthers ? allRows.filter(isOtherPerson) : [];
  const rows = hidden.length ? allRows.filter((r) => !isOtherPerson(r)) : allRows;
  const hiddenTotal = hidden.reduce((n, r) => n + r.total, 0);
  const shownTotal = rows.reduce((n, r) => n + r.total, 0);
  const officeById = useMemo(() => {
    const out = {};
    (offices || []).forEach((o) => {
      out[String(o.id)] = o;
    });
    return out;
  }, [offices]);

  // Scale the bars to the busiest VISIBLE person. Scaling to Unassigned's 69
  // would squash the whole team into indistinguishable stubs and hide the
  // ranking; scaling to a folded-away office row would do the same, quietly.
  const peak = useMemo(
    () => Math.max(1, ...rows.filter((r) => !SPECIAL[r.actor]).map((r) => r.total)),
    [rows],
  );

  const columns = useMemo(() => {
    const cols = [
      { title: actorHeader, key: "actor", width: 264, render: (_, r) => <ActorCell row={r} /> },
      {
        title: "Total",
        key: "total",
        width: 116,
        align: "right",
        render: (_, r) => <TotalCell row={r} peak={peak} kind={kind} />,
      },
    ];
    // Every type keeps its column even when the whole day is zero. This table is
    // read daily, and a column that vanishes is a question ("where did follow-ups
    // go?") rather than a tidier layout.
    types.forEach((t) => {
      cols.push({
        title: TYPE_LABELS[t] || t,
        key: `type-${t}`,
        // "BREAKDOWN" at the house header size needs this much or the headers touch.
        width: 106,
        align: "right",
        render: (_, r) => (
          <Num
            value={r.by_type[t] || 0}
            split={(r[`type_${kind}`] || {})[t]}
            kind={kind}
            className={r.by_type[t] ? "ftcd-num" : "ftcd-zero"}
          />
        ),
      });
    });
    cols.push({
      title: "Offices",
      key: "offices",
      render: (_, r) => (
        <OfficesCell
          row={r}
          officeById={officeById}
          types={types}
          kind={kind}
          onChipClick={onChipClick}
        />
      ),
    });
    return cols;
  }, [types, officeById, peak, actorHeader, onChipClick, kind]);

  return (
    <div className="ftcd-sec">
      <div className="ftcd-sec-head">
        <div className="ftcd-sec-title">{title}</div>
        <div className="crm-sub">{subtitle}</div>
        <div className="ftcd-sec-right">
          {extra}
          <span className="crm-sub">
            {hidden.length ? `${shownTotal} of ${(data && data.total) || 0}` : `${(data && data.total) || 0} total`}
          </span>
        </div>
      </div>
      <div className="crm-table-card">
        <Table
          rowKey="actor"
          size="small"
          tableLayout="fixed"
          columns={columns}
          dataSource={rows}
          pagination={false}
          // Only scrolls on a genuinely narrow screen; the office chips wrap
          // rather than pushing the table sideways.
          scroll={{ x: 880 }}
          locale={{
            emptyText: <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          }}
          footer={
            foldOthers && (hidden.length || showOthers)
              ? () => (
                  <button type="button" className="ftcd-fold" onClick={() => setShowOthers((v) => !v)}>
                    {showOthers
                      ? "Show only FTC personnel"
                      : `Show ${hidden.length} handled by ${hidden.length === 1 ? "an office account" : "office accounts"} (${hiddenTotal})`}
                  </button>
                )
              : undefined
          }
        />
      </div>
    </div>
  );
}

export default function FtcDashboard({ reloadTick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drilling, setDrilling] = useState(null);
  // null means "today" — left unset so the page follows the clock over a long
  // session and a reload never pins yesterday.
  const [day, setDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getFtcDashboard(day ? day.format("YYYY-MM-DD") : null);
      setData(res.status >= 200 && res.status < 300 ? res.json : null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => {
    load();
  }, [load, reloadTick]);

  if (loading && !data)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin />
      </div>
    );
  if (!data) return <Empty description="Couldn't load the dashboard." />;

  const types = data.types || [];
  const offices = data.offices || [];
  const isToday = data.is_today !== false;
  const uncovered = offices.filter((o) => o.covered === false);

  // Handled is history — it opens the exact requests behind the number.
  const openHandled = (row, item) =>
    setDrilling({
      date: data.day,
      actor: row.actor,
      who: (row.person && row.person.name) || (SPECIAL[row.actor] || {}).label || "",
      officeId: item.id,
      officeName: item.name,
    });

  // Outstanding is a queue you act on, so it leaves for the place you act:
  // FTC Tasks, narrowed to the same office AND the same assignee the chip
  // counted. Without the assignee the destination would be bigger than the
  // number that sent you there.
  const openOutstanding = (row, item) => {
    const who = row.person && row.person.name;
    const q = [
      `office=${encodeURIComponent(item.id)}`,
      row.person ? `assignee=${encodeURIComponent(row.person.id)}` : "",
      who ? `who=${encodeURIComponent(who)}` : "",
    ].filter(Boolean).join("&");
    window.location.hash = `#/ftc-tasks?${q}`;
  };

  const datePicker = (
    <>
      {!isToday ? (
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setDay(null)}>
          Back to today
        </Button>
      ) : null}
      <DatePicker
        size="small"
        allowClear={false}
        // Clearing to null would be indistinguishable from "today" in the picker
        // while the button above already says it, so the value is always shown.
        value={day || dayjs()}
        onChange={(d) => setDay(d && d.isSame(dayjs(), "day") ? null : d)}
        // A future day can only ever be empty; the server rejects it too.
        disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
        style={{ width: 140 }}
      />
      {loading ? <Spin size="small" /> : null}
    </>
  );

  return (
    <div>
      <Section
        title={isToday ? "Handled today" : "Handled"}
        subtitle={
          <span>
            {isToday ? data.day : `on ${data.day}`}
            {" · "}
            <Tooltip title="Requests count on the day they were marked done (Verified / Estimate Available / Follow-up Done). Proactive work has no request to mark, so it counts on the day it was created.">
              <span style={{ borderBottom: "1px dotted #cfcad9", cursor: "default" }}>how days are counted</span>
            </Tooltip>
          </span>
        }
        actorHeader="Handled by"
        data={data.handled}
        types={types}
        offices={offices}
        extra={datePicker}
        foldOthers
        onChipClick={openHandled}
        emptyText={isToday ? "Nothing closed yet today." : "Nothing was closed that day."}
      />
      <Section
        title="Outstanding"
        subtitle="on our desk right now, busiest first — not affected by the date above"
        actorHeader="Assigned to"
        data={data.outstanding}
        types={types}
        offices={offices}
        onChipClick={openOutstanding}
        kind="carrier"
        emptyText="Nothing outstanding."
      />
      {uncovered.length ? (
        <div className="ftcd-note" style={{ marginTop: -18 }}>
          <WarningOutlined className="ftcd-note-mark" style={{ color: "#fa8c16" }} />
          <div>
            {uncovered.length === 1 ? "This office has" : "These offices have"} no FTC with access,
            so their open work can&rsquo;t be assigned to anyone:{" "}
            <b>{uncovered.map((o) => o.name || `Office ${o.id}`).join(", ")}</b>. Grant access on
            the FTC users tab.
          </div>
        </div>
      ) : null}
      <HandledDrawer target={drilling} onClose={() => setDrilling(null)} />
    </div>
  );
}
